import FootballStatsCollection from "./football-stats-collection";
import { getByPath } from "../../../services/common/object-util";
import { resolvePlayerName } from "../../common/utils/player-utils";

export default class FootballPlayerStatsCollection extends FootballStatsCollection {
    constructor(player, statLogs = []) {
        super(
            statLogs.filter((s) => {
                return s.playerId === player.id;
            })
        );
        this.player = player;
    }

    getMiscStats(statsSummary) {
        const { stats } = statsSummary || {};
        const {
            completed: continueCompleted,
            totalYards: continueTotalYards,
            averageYards: continueAverageYards
        } = getByPath(stats, "misc.continue") || {};
        const {
            completed: lateralCompleted,
            totalYards: lateralTotalYards,
            averageYards: lateralAverageYards
        } = getByPath(stats, "misc.lateral") || {};
        const {
            completed: fgReturnCompleted,
            totalYards: fgReturnTotalYards,
            averageYards: fgReturnAverageYards
        } = getByPath(stats, "misc.fgReturn") || {};

        const hasStats = [
            continueCompleted,
            lateralCompleted,
            fgReturnCompleted
        ].some((v) => v);

        return {
            completed: continueCompleted + lateralCompleted + fgReturnCompleted,
            totalYards:
                continueTotalYards + lateralTotalYards + fgReturnTotalYards,
            averageYards:
                continueAverageYards +
                lateralAverageYards +
                fgReturnAverageYards,
            hasStats
        };
    }

    getPassingStats(statsSummary) {
        const { stats } = statsSummary || {};
        const {
            completed,
            attempted,
            totalYards,
            longestYard,
            touchdowns,
            interceptions,
            percent
        } = getByPath(stats, "offense.pass") || {};
        const sack = getByPath(stats, "offense.sack.completed") || 0;
        const hasStats = [attempted, sack].some((v) => v);

        return {
            completed,
            attempted,
            totalYards,
            longestYard,
            touchdowns,
            interceptions,
            percent: percent.toFixed(2),
            sack,
            hasStats
        };
    }

    getDefenseStats(statsSummary) {
        const { stats } = statsSummary || {};

        const blocks = getByPath(stats, "defense.blocked.completed");
        const sacks = getByPath(stats, "defense.sack.completed");
        const interceptions = getByPath(
            stats,
            "defense.interceptions.completed"
        );
        const interceptionYards = getByPath(
            stats,
            "defense.interceptionReturns.totalYards"
        );
        const tackles = getByPath(stats, "defense.tackle.completed");
        const tfl = getByPath(stats, "defense.tackleForLoss.completed");
        const tflYards = getByPath(stats, "defense.tackleForLoss.lostYards");
        const ff = getByPath(stats, "defense.fumble.forced.completed");
        const fr = getByPath(stats, "defense.fumble.recovered.completed");
        const tdfi = getByPath(stats, "defense.interceptionReturns.touchdowns");
        const fumbleReturnedYards = getByPath(
            stats,
            "defense.fumble.returned.gainedYards"
        );
        const fumbleReturnedTouchdowns = getByPath(
            stats,
            "defense.fumble.continued.touchdowns"
        );

        const passBrokenUp = getByPath(stats, "defense.passBrokenUp.completed");

        const hasStats = [
            tackles,
            sacks,
            interceptions,
            interceptionYards,
            tdfi,
            ff,
            fr,
            fumbleReturnedYards,
            fumbleReturnedTouchdowns,
            blocks,
            passBrokenUp
        ].some((v) => v);

        return {
            tackles,
            tfl,
            tflYards,
            sacks,
            interceptions,
            interceptionYards,
            ff,
            fr,
            tdfi,
            fumbleReturnedYards,
            fumbleReturnedTouchdowns,
            hasStats,
            blocks,
            passBrokenUp
        };
    }

    getPuntingStats(statsSummary) {
        const { stats } = statsSummary || {};
        const {
            completed,
            totalYards,
            longestYard,
            touchback,
            averageYards,
            in20,
            rouge
        } = getByPath(stats, "specialTeams.punt") || {};

        const hasStats = [
            completed,
            totalYards,
            longestYard,
            touchback,
            averageYards,
            in20,
            rouge
        ].some((v) => v);

        return {
            completed,
            totalYards,
            longestYard,
            touchback,
            averageYards: averageYards.toFixed(2),
            in20,
            rouge,
            hasStats
        };
    }

    getPuntReturnedStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed, totalYards, longestYard, touchdowns, averageYards } =
            getByPath(stats, "specialTeams.puntReturn") || {};
        const hasStats = [completed].some((v) => v);

        return {
            completed,
            totalYards,
            longestYard,
            touchdowns,
            averageYards: averageYards.toFixed(2),
            hasStats
        };
    }

    getReceivingStats(statsSummary) {
        const { stats } = statsSummary || {};
        const {
            completed,
            attempted,
            percent,
            totalYards,
            longestYard,
            averageYards,
            touchdowns
        } = getByPath(stats, "offense.passTarget") || {};
        const hasStats = [attempted].some((v) => v);

        return {
            completed,
            attempted,
            percent: percent.toFixed(2),
            totalYards,
            longestYard,
            averageYards: averageYards.toFixed(2),
            touchdowns,
            hasStats
        };
    }

    getReturnedStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed, totalYards, longestYard, touchdowns, averageYards } =
            getByPath(stats, "defense.returns") || {};
        const hasStats = [completed].some((v) => v);

        return {
            completed,
            totalYards,
            longestYard,
            touchdowns,
            averageYards: averageYards.toFixed(2),
            hasStats
        };
    }

    getRushingStats(statsSummary) {
        const { stats } = statsSummary || {};
        const {
            completed,
            gainedYards,
            lostYards,
            totalYards,
            longestYard,
            averageYards,
            touchdowns
        } = getByPath(stats, "offense.rush") || {};
        const { completed: kdCompleted, totalYards: kdTotalYards } =
            getByPath(stats, "offense.kneelDown") || {};
        const hasStats = [completed, kdCompleted].some((v) => v);

        return {
            completed: completed + kdCompleted,
            gainedYards: gainedYards - Math.abs(kdTotalYards),
            lostYards,
            totalYards,
            longestYard,
            averageYards: averageYards.toFixed(2),
            touchdowns,
            hasStats
        };
    }

    getPenaltyStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed, totalYards, absoluteYards } =
            getByPath(stats, "penalty") || {};
        const hasStats = [completed, totalYards].some((v) => v);

        return {
            completed,
            totalYards,
            hasStats,
            absoluteYards
        };
    }

    getKickingStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed, totalYards, averageYards, touchback, rouge } =
            getByPath(stats, "kicks.kickoff") || {};
        const hasStats = [completed, rouge].some((v) => v);

        return {
            completed,
            totalYards,
            averageYards: averageYards.toFixed(2),
            touchback,
            rouge,
            hasStats
        };
    }

    getKickingReturnStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed, totalYards, longestYard, averageYards, touchdowns } =
            getByPath(stats, "kicks.kickoffReturn") || {};
        const hasStats = [completed].some((v) => v);

        return {
            completed,
            totalYards,
            averageYards: averageYards.toFixed(2),
            longestYard,
            touchdowns,
            hasStats
        };
    }

    getFieldGoalStats(statsSummary) {
        const { stats } = statsSummary || {};
        const {
            completed,
            attempted,
            longestYard,
            rouge,
            dropKick: dropKickAttempted,
            dropKickCompleted
        } = getByPath(stats, "kicks.fieldGoal") || {};
        const {
            completed: patCompleted,
            attempted: patAttempted,
            dropKick: patDropKickAttempted,
            dropKickCompleted: patDropKickCompleted
        } = getByPath(stats, "kicks.patKick") || {};
        const hasStats = [
            completed,
            attempted,
            patCompleted,
            patAttempted,
            rouge
        ].some((v) => v);

        return {
            completed,
            attempted,
            longestYard,
            patCompleted,
            patAttempted,
            rouge,
            // the summarizer omits these keys when no drop kick was taken
            dropKickAttempted: dropKickAttempted || 0,
            dropKickCompleted: dropKickCompleted || 0,
            patDropKickAttempted: patDropKickAttempted || 0,
            patDropKickCompleted: patDropKickCompleted || 0,
            hasStats
        };
    }

    getFieldGoalRedzoneStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed, attempted, longestYard } =
            getByPath(stats, "kicks.fieldGoalRedzone") || {};
        const hasStats = [completed, attempted].some((v) => v);

        return {
            completed,
            attempted,
            longestYard,
            hasStats
        };
    }

    getFieldGoalBlockedStats(statsSummary) {
        const { stats } = statsSummary || {};
        const { completed } = getByPath(stats, "kicks.fieldGoalBlocked") || {};
        const hasStats = [completed].some((v) => v);

        return {
            completed,
            hasStats
        };
    }

    toJSON() {
        const statsSummary = super.toJSON();

        return {
            ...statsSummary,
            id: this.player.id,
            firstName: this.player.firstName,
            lastName: this.player.lastName,
            uniform: this.player.uniform,
            fullName: resolvePlayerName(this.player),
            /***
             * The stats below could also be obtained from statsSummary directly.
             * These are just shortcuts to access them.
             ***/
            passing: this.getPassingStats(statsSummary),
            defense: this.getDefenseStats(statsSummary),
            punting: this.getPuntingStats(statsSummary),
            puntReturn: this.getPuntReturnedStats(statsSummary),
            receiving: this.getReceivingStats(statsSummary),
            returned: this.getReturnedStats(statsSummary),
            rushing: this.getRushingStats(statsSummary),
            penalty: this.getPenaltyStats(statsSummary),
            kickoff: this.getKickingStats(statsSummary),
            kickoffReturn: this.getKickingReturnStats(statsSummary),
            fieldGoal: this.getFieldGoalStats(statsSummary),
            fieldGoalRedzone: this.getFieldGoalRedzoneStats(statsSummary),
            fieldGoalBlocked: this.getFieldGoalBlockedStats(statsSummary),
            misc: this.getMiscStats(statsSummary)
        };
    }
}
